import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Campaign, Flag, Save, Verified } from '@mui/icons-material';
import {
  BEST_TIME_OPTIONS,
  CONSENT_OPTIONS,
  LEAD_TEMPERATURE_OPTIONS,
  PREFERRED_CHANNEL_OPTIONS,
  PROFESSIONALISM_OPTIONS,
  QUALITY_FIELDS,
  RED_FLAGS,
  RESPONSIVENESS_OPTIONS,
  TONE_COLORS,
  getQuality,
  getReadiness,
} from './activityConfig';

const isCompany = (c) => c?.contactType === 'company';
const channelValue = (contact, channel) => {
  if (!contact) return '';
  if (channel === 'email') return isCompany(contact) ? contact.companyEmail : contact.email;
  if (channel === 'phone') return isCompany(contact) ? contact.companyPhone : contact.phoneNumber;
  if (channel === 'whatsapp') return isCompany(contact) ? contact.companyWhatsapp : contact.whatsappNumber;
  if (channel === 'website') return contact.website;
  return '';
};

const SectionLabel = ({ icon: Icon, children }) => (
  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.25 }}>
    {Icon && <Icon sx={{ fontSize: 18, color: '#3b82f6' }} />}
    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
      {children}
    </Typography>
  </Stack>
);

// Single-choice chips (click again to clear)
const ChipChoice = ({ options, value, onChange, allowClear = true }) => (
  <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
    {options.map((option) => {
      const selected = value === option.value;
      const color = option.color || TONE_COLORS[option.tone] || '#64748b';
      return (
        <Chip
          key={option.value}
          size="small"
          clickable
          label={option.label}
          aria-pressed={selected}
          onClick={() => onChange(selected && allowClear ? '' : option.value)}
          sx={{
            fontWeight: 600,
            bgcolor: selected ? `${color}22` : '#fff',
            color: selected ? '#1e293b' : '#64748b',
            border: `1px solid ${selected ? color : '#e2e8f0'}`,
            '&:hover': { bgcolor: `${color}14 !important` },
          }}
        />
      );
    })}
  </Stack>
);

const ToneDot = ({ tone }) => (
  <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TONE_COLORS[tone] || TONE_COLORS.muted, display: 'inline-block', mr: 1, flexShrink: 0 }} />
);

const DataQualityPanel = ({ contact, onSave, saving, currentUser }) => {
  const saved = useMemo(() => getQuality(contact), [contact]);
  const [draft, setDraft] = useState(saved);

  // Reset the draft only when the stored values really change (after saving or reloading),
  // not when something else on the contact updates – so unsaved edits survive adding a note
  const savedKey = JSON.stringify(saved);
  useEffect(() => setDraft(JSON.parse(savedKey)), [savedKey]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const readiness = getReadiness({ dataQuality: draft });
  const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  const toggleFlag = (flag) =>
    set({ redFlags: draft.redFlags.includes(flag) ? draft.redFlags.filter((f) => f !== flag) : [...draft.redFlags, flag] });

  const handleSave = () =>
    onSave({
      ...draft,
      reviewedBy: currentUser?.name || '',
      reviewedAt: new Date().toISOString(),
    });

  return (
    <Paper sx={{ borderRadius: 3, border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 2, borderBottom: '1px solid #f1f5f9' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Campaign sx={{ color: '#3b82f6' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.05rem' }}>
            Marketing & data quality
          </Typography>
        </Stack>
        <Chip
          size="small"
          label={readiness.label}
          sx={{ fontWeight: 700, bgcolor: `${readiness.color}1f`, color: readiness.color, border: `1px solid ${readiness.color}55` }}
        />
      </Stack>

      <Box sx={{ px: 2.5, py: 2, flex: 1, overflowY: 'auto', maxHeight: { md: 640 } }}>
        {/* Channel verification */}
        <SectionLabel icon={Verified}>Contact details checked</SectionLabel>
        <Stack spacing={1.25}>
          {QUALITY_FIELDS.map((field) => {
            const value = channelValue(contact, field.channel);
            const selected = field.options.find((o) => o.value === draft[field.key]);
            return (
              <Stack key={field.key} direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ width: 96, flexShrink: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                    {field.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: value ? '#64748b' : '#cbd5e1', display: 'block' }} noWrap title={value || ''}>
                    {value || 'Not provided'}
                  </Typography>
                </Box>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={draft[field.key]}
                  onChange={(e) => set({ [field.key]: e.target.value })}
                  SelectProps={{
                    renderValue: () => (
                      <Stack direction="row" alignItems="center">
                        <ToneDot tone={selected?.tone} />
                        {selected?.label}
                      </Stack>
                    ),
                  }}
                  inputProps={{ 'aria-label': `${field.label} status` }}
                >
                  {field.options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <ToneDot tone={option.tone} />
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            );
          })}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Engagement */}
        <SectionLabel>How they engage</SectionLabel>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.75 }}>Responsiveness</Typography>
            <ChipChoice options={RESPONSIVENESS_OPTIONS} value={draft.responsiveness} onChange={(v) => set({ responsiveness: v })} />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.75 }}>Professionalism</Typography>
            <ChipChoice options={PROFESSIONALISM_OPTIONS} value={draft.professionalism} onChange={(v) => set({ professionalism: v })} />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.75 }}>Lead temperature</Typography>
            <ChipChoice options={LEAD_TEMPERATURE_OPTIONS} value={draft.leadTemperature} onChange={(v) => set({ leadTemperature: v })} />
          </Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Overall data quality</Typography>
            <Rating size="small" value={Number(draft.rating) || 0} onChange={(e, v) => set({ rating: v || 0 })} />
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Marketing */}
        <SectionLabel icon={Campaign}>Marketing preferences</SectionLabel>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.75 }}>Marketing consent</Typography>
            <ChipChoice options={CONSENT_OPTIONS} value={draft.marketingConsent} onChange={(v) => set({ marketingConsent: v || 'not_asked' })} allowClear={false} />
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField select size="small" fullWidth label="Best channel" value={draft.preferredChannel} onChange={(e) => set({ preferredChannel: e.target.value })}>
              <MenuItem value=""><em>Not set</em></MenuItem>
              {PREFERRED_CHANNEL_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
            <TextField select size="small" fullWidth label="Best time" value={draft.bestTimeToContact} onChange={(e) => set({ bestTimeToContact: e.target.value })}>
              <MenuItem value=""><em>Not set</em></MenuItem>
              {BEST_TIME_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField
            size="small"
            fullWidth
            label="Interested in"
            placeholder="e.g. ERP, Social media marketing, Website redesign"
            helperText="Products or topics to target in campaigns (comma separated)"
            value={draft.interests}
            onChange={(e) => set({ interests: e.target.value })}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Red flags */}
        <SectionLabel icon={Flag}>Red flags</SectionLabel>
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
          {RED_FLAGS.map((flag) => {
            const selected = draft.redFlags.includes(flag.value);
            return (
              <Chip
                key={flag.value}
                size="small"
                clickable
                label={flag.label}
                aria-pressed={selected}
                onClick={() => toggleFlag(flag.value)}
                icon={selected ? <Flag sx={{ fontSize: '14px !important', color: '#ef4444 !important' }} /> : undefined}
                sx={{
                  fontWeight: 600,
                  bgcolor: selected ? '#fef2f2' : '#fff',
                  color: selected ? '#b91c1c' : '#64748b',
                  border: `1px solid ${selected ? '#fca5a5' : '#e2e8f0'}`,
                }}
              />
            );
          })}
        </Stack>
      </Box>

      {/* Footer */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #f1f5f9', bgcolor: dirty ? '#eff6ff' : '#fff' }}>
        <Typography variant="caption" sx={{ color: '#64748b', flex: 1 }}>
          {dirty
            ? 'You have unsaved changes'
            : saved.reviewedAt
            ? `Last reviewed by ${saved.reviewedBy || 'someone'} · ${new Date(saved.reviewedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
            : 'Not reviewed yet'}
        </Typography>
        {dirty && (
          <Button size="small" onClick={() => setDraft(saved)} disabled={saving} sx={{ textTransform: 'none' }}>
            Discard
          </Button>
        )}
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={!dirty || saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
          onClick={handleSave}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Save
        </Button>
      </Stack>
    </Paper>
  );
};

export default DataQualityPanel;
